import { useOutletContext, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import AudiologistMetricsBar from "../../components/audiologist/AudiologistMetricsBar";
import OrdersTabs from "../orders/OrdersTabs";
import SelectSkuModal from "../../components/va/SelectSkuModal";
import SelectBundleModal from "../../components/va/SelectBundleModal";
import UploadPrescriptionStepModal from "../../components/va/UploadPrescriptionStepModal";
import SelectLensesModal from "../../components/va/SelectLensesModal";
import OrderConfirmationModal from "../../components/va/OrderConfirmationModal";
import {
  useGetVaAudiologistBundlesQuery,
  useGetVaAudiologistSkusQuery,
} from "../../app/vaApi/vaApi";
import { normalizeLensItems } from "../../helpers/prescriptionFormat";
import "../orders/order.css";
import "../distributor/distributor.css";
import "./audiologist.css";

function bundleHasLenses(bundle) {
  return normalizeLensItems(bundle?.lensesItems).length > 0;
}

export default function AudiologistHome() {
  const { intialSearch, setIntialSearch } = useOutletContext() ?? {};
  const [searchParams, setSearchParams] = useSearchParams();
  const createMode = searchParams.get("create");
  const skuFromUrl = searchParams.get("sku");
  const productIdFromUrl = searchParams.get("productId");
  const bundleFromUrl = searchParams.get("bundle");
  const step = searchParams.get("step");
  const [prescriptionData, setPrescriptionData] = useState(null);
  const [lensSelection, setLensSelection] = useState(null);
  const [selectedSku, setSelectedSku] = useState(null);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [confirmationDelivery, setConfirmationDelivery] = useState(null);

  const { data: skuOptions = [] } = useGetVaAudiologistSkusQuery(undefined, {
    skip: createMode !== "manual",
  });

  const resolvedProductId =
    selectedSku?.productId || productIdFromUrl || null;

  const { data: bundleOptions = [] } = useGetVaAudiologistBundlesQuery(
    resolvedProductId,
    {
      skip: createMode !== "manual" || !resolvedProductId,
    }
  );

  // Restore selected SKU after refresh / direct URL
  useEffect(() => {
    if (!skuFromUrl || selectedSku?.code === skuFromUrl) return;
    const matched =
      skuOptions.find(
        (s) =>
          String(s.code).toUpperCase() === String(skuFromUrl).toUpperCase() ||
          String(s.productId) === String(productIdFromUrl || "")
      ) || null;
    if (matched) {
      setSelectedSku(matched);
    } else if (productIdFromUrl) {
      setSelectedSku({
        code: skuFromUrl,
        productId: productIdFromUrl,
        label: skuFromUrl,
      });
    }
  }, [skuFromUrl, productIdFromUrl, skuOptions, selectedSku?.code]);

  // Restore selected bundle after refresh / direct URL
  useEffect(() => {
    if (!bundleFromUrl) return;
    if (
      selectedBundle?.code &&
      String(selectedBundle.code).toUpperCase() ===
        String(bundleFromUrl).toUpperCase()
    ) {
      return;
    }
    const matched =
      bundleOptions.find(
        (b) =>
          String(b.code).toUpperCase() === String(bundleFromUrl).toUpperCase()
      ) || null;
    if (matched) {
      setSelectedBundle(matched);
    }
  }, [bundleFromUrl, bundleOptions, selectedBundle?.code]);

  const hasLenses = useMemo(
    () => bundleHasLenses(selectedBundle),
    [selectedBundle]
  );

  // No-lens bundles: skip prescription + select-lenses → go straight to confirmation
  useEffect(() => {
    if (createMode !== "manual" || !bundleFromUrl || !selectedBundle) return;
    if (
      String(selectedBundle.code).toUpperCase() !==
      String(bundleFromUrl).toUpperCase()
    ) {
      return;
    }
    if (hasLenses) return;
    if (step === "confirmation") return;

    setPrescriptionData((prev) => prev || { skipped: true });
    setLensSelection(null);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("step", "confirmation");
        return params;
      },
      { replace: true }
    );
  }, [
    createMode,
    bundleFromUrl,
    selectedBundle,
    hasLenses,
    step,
    setSearchParams,
  ]);

  const closeCreateModal = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("create");
    params.delete("parseId");
    params.delete("sku");
    params.delete("productId");
    params.delete("bundle");
    params.delete("step");
    setSearchParams(params);
    setPrescriptionData(null);
    setLensSelection(null);
    setSelectedSku(null);
    setSelectedBundle(null);
    setConfirmationDelivery(null);
  };

  const handleSkuSelect = (skuCode, sku = null) => {
    const nextSku = sku || { code: skuCode };
    setSelectedSku(nextSku);
    setSelectedBundle(null);
    setPrescriptionData(null);
    setLensSelection(null);
    setConfirmationDelivery(null);
    const params = new URLSearchParams(searchParams);
    params.set("create", "manual");
    params.set("sku", skuCode);
    if (nextSku.productId) {
      params.set("productId", String(nextSku.productId));
    } else {
      params.delete("productId");
    }
    params.delete("bundle");
    params.delete("step");
    setSearchParams(params);
  };

  const handleBundleSelect = (bundleCode, bundle = null) => {
    const nextBundle = bundle || { code: bundleCode, includesLenses: false };
    setSelectedBundle(nextBundle);
    setLensSelection(null);
    setConfirmationDelivery(null);

    const params = new URLSearchParams(searchParams);
    params.set("create", "manual");
    params.set("bundle", bundleCode);

    if (bundleHasLenses(nextBundle)) {
      setPrescriptionData(null);
      params.delete("step");
    } else {
      setPrescriptionData({ skipped: true });
      params.set("step", "confirmation");
    }

    setSearchParams(params);
  };

  const handlePrescriptionContinue = (prescription) => {
    setPrescriptionData(prescription);
    const params = new URLSearchParams(searchParams);
    params.set("step", "lenses");
    setSearchParams(params);
  };

  const handleLensesConfirm = (selection) => {
    setLensSelection(selection);
    const params = new URLSearchParams(searchParams);
    params.set("step", "confirmation");
    setSearchParams(params);
  };

  const handleConfirmationBack = () => {
    const params = new URLSearchParams(searchParams);
    if (hasLenses) {
      params.set("step", "lenses");
    } else {
      params.delete("bundle");
      params.delete("step");
      setSelectedBundle(null);
      setPrescriptionData(null);
      setLensSelection(null);
    }
    setSearchParams(params);
  };

  const handleLensesBack = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("step");
    setSearchParams(params);
  };

  const handlePrescriptionBack = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("bundle");
    params.delete("step");
    setSearchParams(params);
    setSelectedBundle(null);
  };

  const handleBundleBack = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("sku");
    params.delete("productId");
    params.delete("bundle");
    setSearchParams(params);
    setSelectedSku(null);
  };

  const openCreateModal = (mode) => {
    const params = new URLSearchParams(searchParams);
    params.set("create", mode);
    setSearchParams(params);
  };

  const showSkuModal = createMode === "manual" && !skuFromUrl;
  const showBundleModal =
    createMode === "manual" && Boolean(skuFromUrl) && !bundleFromUrl;
  const showPrescriptionModal =
    createMode === "manual" &&
    Boolean(bundleFromUrl) &&
    hasLenses &&
    !step;
  const showLensesModal =
    createMode === "manual" &&
    Boolean(bundleFromUrl) &&
    hasLenses &&
    step === "lenses";
  const showConfirmationModal =
    createMode === "manual" &&
    Boolean(bundleFromUrl) &&
    step === "confirmation";

  return (
    <div className="audiologist-dashboard orders-page">
      <AudiologistMetricsBar />
      <OrdersTabs
        variant="audiologist"
        showStatusCards={false}
        intialSearch={intialSearch ?? ""}
        setIntialSearch={setIntialSearch}
        onOpenCreateManual={() => openCreateModal("manual")}
      />

      {showSkuModal && (
        <SelectSkuModal
          show
          onClose={closeCreateModal}
          onSelect={handleSkuSelect}
        />
      )}
      {showBundleModal && (
        <SelectBundleModal
          show
          onClose={closeCreateModal}
          onBack={handleBundleBack}
          onSelect={handleBundleSelect}
          productId={resolvedProductId}
          skuLabel={selectedSku?.label}
        />
      )}
      {showPrescriptionModal && (
        <UploadPrescriptionStepModal
          show
          onClose={closeCreateModal}
          onBack={handlePrescriptionBack}
          onContinue={handlePrescriptionContinue}
          initialPrescription={prescriptionData}
          onDraftChange={setPrescriptionData}
          lensItems={selectedBundle?.lensesItems || []}
        />
      )}
      {showLensesModal && (
        <SelectLensesModal
          show
          onClose={closeCreateModal}
          onBack={handleLensesBack}
          onConfirm={handleLensesConfirm}
          initialSelection={lensSelection}
          onSelectionChange={setLensSelection}
          prescription={prescriptionData?.skipped ? null : prescriptionData}
          prescriptionSkipped={Boolean(prescriptionData?.skipped)}
          lensItems={selectedBundle?.lensesItems || []}
        />
      )}
      {showConfirmationModal && (
        <OrderConfirmationModal
          show
          onClose={closeCreateModal}
          onBack={handleConfirmationBack}
          skuCode={skuFromUrl}
          skuLabel={selectedSku?.label}
          skuPrice={selectedSku?.price}
          bundleCode={bundleFromUrl}
          bundle={selectedBundle}
          lensSelection={lensSelection}
          prescriptionData={prescriptionData}
          initialDelivery={confirmationDelivery}
          onDeliveryChange={setConfirmationDelivery}
        />
      )}
    </div>
  );
}
